'use client'

import { AnimatePresence, motion } from 'motion/react'
import Image from 'next/image'
import { useParams, usePathname } from 'next/navigation'
import { useLocale } from 'next-intl'
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/button'
import { TopPageBanner } from '@/components/top-page-banner'
import { INavigationDesktopItemProps } from '@/features/navigation/navigation-desktop-item'
import { NavigationMobileItem } from '@/features/navigation/navigation-mobile-item'
import { RestaurantCityMobileNavigationHeader } from '@/features/restaurants/restaurants-city-search/restaurant-city-mobile-navigation-header'
import { SvgOutlinedMenu } from '@/icons/svg-outlined-menu'
import { Language, Locale, parseLanguageFromLocale } from '@/setup/locales'
import { getPathname, UrlConfig } from '@/setup/routing'
import { RestaurantUrlParams } from '@/setup/urls/restaurant-route'
import { useCitySlugUrlParam } from '@/setup/urls/use-city-slug-url-param'
import { throttle } from '@/utils/functions/throttle'
import { run } from '@/utils/run'
import { cn } from '@/utils/styles/cn'
import { Z_INDEX } from '@/utils/styles/z-index'
import { useMobile } from '@/utils/window/use-mobile'

interface INavigationMobileProps {
    items: Omit<INavigationDesktopItemProps, 'isActive'>[]
}

export const NavigationMobile = ({ items }: INavigationMobileProps) => {
    const locale = useLocale()
    const [canReactToScroll, setCanReactToScroll] = useState(false)

    const citySlug = useCitySlugUrlParam()

    const [isMenuOpen, setIsMenuOpen] = useState(false)

    const [visible, setVisible] = useState(true)

    const params = useParams()
    const pathname = usePathname()

    const isMobile = useMobile()

    // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is a trigger, not a value the body reads
    useLayoutEffect(() => {
        if (!isMobile) return

        setVisible(true)
        setIsMenuOpen(false)

        document.body.style.overflow = 'auto'
    }, [isMobile, pathname])

    const prevScrollPosRef = useRef(0)

    const ref = useRef<HTMLDivElement>(null)

    const [isRestaurantsCityUrl, setIsRestaurantsCityUrl] = useState(() =>
        Boolean(citySlug && !params[RestaurantUrlParams.RESTAURANT_SLUG])
    )

    const [isRestaurantDetailUrl, setIsRestaurantDetailUrl] = useState(() =>
        Boolean(citySlug && params[RestaurantUrlParams.RESTAURANT_SLUG])
    )

    useEffect(() => {
        const nextIsRestaurantsCityUrl = Boolean(
            citySlug && !params[RestaurantUrlParams.RESTAURANT_SLUG]
        )

        if (isRestaurantsCityUrl === nextIsRestaurantsCityUrl) return

        setIsRestaurantsCityUrl(nextIsRestaurantsCityUrl)
    }, [citySlug, params, isRestaurantsCityUrl])

    useEffect(() => {
        const nextIsRestaurantDetailUrl = Boolean(
            citySlug && params[RestaurantUrlParams.RESTAURANT_SLUG]
        )

        if (isRestaurantDetailUrl === nextIsRestaurantDetailUrl) return

        setIsRestaurantDetailUrl(nextIsRestaurantDetailUrl)
    }, [citySlug, params, isRestaurantDetailUrl])

    const dangerouslyCalculateVisibility = useCallback(() => {
        if (!canReactToScroll || isRestaurantsCityUrl) return

        // If something is currently shown above the navigation bar, we don't want to hide the navigation (hiding = moving it up = looks weird)
        const navigationTopOffset = ref.current?.getBoundingClientRect().top ?? 0

        // If url code is present, we don't want to hide the navigation, as the code is shown above the navigation bar
        if (navigationTopOffset > 0) return

        const scrollY = window.scrollY

        const shouldBeVisible = prevScrollPosRef.current > scrollY || scrollY < 100

        setVisible(shouldBeVisible)
        prevScrollPosRef.current = scrollY
    }, [canReactToScroll, isRestaurantsCityUrl])

    const onScroll = useMemo(
        () => throttle(() => dangerouslyCalculateVisibility(), 300),
        [dangerouslyCalculateVisibility]
    )

    const isInitialRender = useRef(true)

    useEffect(() => {
        if (isInitialRender.current) {
            dangerouslyCalculateVisibility()
            isInitialRender.current = false
        }

        window.addEventListener('scroll', onScroll)

        return () => window.removeEventListener('scroll', onScroll)
    }, [onScroll, dangerouslyCalculateVisibility])

    const toggleIsOpen = useCallback((nextIsOpen: boolean) => {
        document.body.style.overflow = nextIsOpen ? 'hidden' : 'auto'

        if (nextIsOpen) {
            const currentScrollY = window.scrollY

            document.body.style.overflow = 'hidden'

            window.scrollTo({
                top: currentScrollY,
                behavior: 'instant',
            })
        } else {
            document.body.style.overflow = 'auto'
        }

        setIsMenuOpen(nextIsOpen)
    }, [])

    useEffect(() => {
        // Wait for sync rendering work to finish until reading the scrollY (otherwise it will always be 0)
        const initialReadTimeout = setTimeout(() => {
            setCanReactToScroll(false)
        }, 0)

        // After url changes, allow for some time to pass before reacting to scroll
        // Otherwise it might happen that auto scroll restoration triggers the navigation to hide / show
        const secondaryTimeout = setTimeout(() => {
            setCanReactToScroll(true)
        }, 800)

        return () => {
            clearTimeout(initialReadTimeout)
            clearTimeout(secondaryTimeout)
        }
    }, [])

    const isForcedHidden = isRestaurantDetailUrl

    const hasTopBanner = getHasTopPageBanner(locale as Locale)

    const isForcedHiddenValue = !hasTopBanner ? 64 : 104
    const topValue = !hasTopBanner ? 'top-0' : 'top-10'
    const calcValue = !hasTopBanner ? 'calc(64px)' : 'calc(104px)'

    return (
        <Fragment>
            <div
                className={cn(
                    'fixed md:relative w-full z-navigation top-0',
                    isForcedHidden && 'hidden'
                )}
            >
                {hasTopBanner && <TopPageBanner />}
            </div>

            <div
                className="md:hidden"
                style={{ height: isForcedHidden ? 0 : isForcedHiddenValue }}
            />

            <nav
                ref={ref}
                className={cn(
                    `z-navigation flex items-center md:hidden w-full bg-white min-h-[64px] px-4 ${topValue} fixed
                    transition-transform duration-300 justify-between`,
                    !visible ? '-translate-y-full' : '',
                    !canReactToScroll && 'transition-none',
                    isForcedHidden && 'hidden'
                )}
            >
                {run(() => {
                    if (isRestaurantsCityUrl) {
                        if (!citySlug) return null

                        return <RestaurantCityMobileNavigationHeader citySlug={citySlug} />
                    }

                    return (
                        <a
                            href={UrlConfig['/'].getUrl()}
                            className="relative block h-[25px] w-[124px] pl-2"
                        >
                            <span className="relative block h-[25px] w-[124px]">
                                <Image
                                    src="/_images/navigation/neotaste-navigation-logo.svg"
                                    alt="NeoTaste Logo"
                                    fill
                                    sizes="124px"
                                    className="object-contain"
                                    priority
                                />
                            </span>
                        </a>
                    )
                })}

                <Button styleType="ghost" onClick={() => toggleIsOpen(!isMenuOpen)}>
                    <SvgOutlinedMenu color="black" size={24} />
                </Button>
            </nav>

            <AnimatePresence initial={false} mode="popLayout">
                {isMenuOpen && (
                    <div className="contents">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{
                                opacity: 0,
                                transition: { opacity: { duration: 0.08 } },
                            }}
                            key="navigation-mobile-menu"
                            transition={{
                                scale: { type: 'spring', duration: 0.3, bounce: 0.1 },
                                opacity: { duration: 0.17 },
                            }}
                            style={{
                                minHeight: 'fit-content',
                                top: calcValue,
                                left: 10,
                                width: 'calc(100vw - 20px)',
                                zIndex: Z_INDEX.navigation + 2,
                            }}
                            className="fixed bg-white px-2 rounded-[20px] pt-2 pb-4 shadow-custom"
                        >
                            {items.map((item) => {
                                const itemPathname =
                                    'externalUrl' in item.linkParams
                                        ? undefined
                                        : getPathname(
                                              item.linkParams.pattern,
                                              item.linkParams.params,
                                              locale as Locale
                                          )

                                const isActive = itemPathname === pathname

                                return (
                                    <NavigationMobileItem
                                        key={'naviation' + item.label}
                                        isActive={isActive}
                                        {...item}
                                    />
                                )
                            })}
                        </motion.div>

                        <motion.div
                            onClick={() => toggleIsOpen(false)}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            key="navigation-menu-background"
                            transition={{ ease: 'easeInOut', duration: 0.3 }}
                            style={{ zIndex: Z_INDEX.navigation + 1 }}
                            className="min-h-dvh min-w-[100vw] fixed bg-black/10 top-0 left-0"
                        />
                    </div>
                )}
            </AnimatePresence>
        </Fragment>
    )
}

export function getHasTopPageBanner(locale: Locale | string) {
    const language = parseLanguageFromLocale(locale)

    return language !== Language.NL && language !== Language.EN && language !== Language.DE
}
